import {
    AmbientLight,
    BoxGeometry,
    CylinderGeometry,
    DirectionalLight,
    Group,
    HemisphereLight,
    Mesh,
    Object3D,
    OrthographicCamera,
    PerspectiveCamera,
    PlaneGeometry,
    PointLight,
    RectAreaLight,
    SphereGeometry,
    SpotLight, Sprite,
    Vector3,
    TubeGeometry, ShaderMaterial, Color, Clock, TextureLoader, MeshPhongMaterial, RepeatWrapping, CurvePath, Curve
} from "three";
import { PandaCommponent, PandaEngine } from "./index.js";
import SpriteText from 'three-spritetext';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { ColladaLoader } from 'three/examples/jsm/loaders/ColladaLoader';
import {DRACOLoader} from "three/examples/jsm/loaders/DRACOLoader";
import {KTX2Loader} from "three/examples/jsm/loaders/KTX2Loader";
import {GLTFLoader} from "three/examples/jsm/loaders/GLTFLoader";
import {MeshoptDecoder} from "three/examples/jsm/libs/meshopt_decoder.module";
import {createPath} from "./utils/util";

export function group(data: PandaCommponent): Object3D {
    const group = new Group();
    group.name = data.name || '组';
    return group;
}

export function floor(data: PandaCommponent): Object3D {
    const textureLoader = new TextureLoader();
    const geometry = new BoxGeometry(data.planeSize, 0, data.planeSize, 1, 1, 1)
    const plane:any = new Mesh(geometry, PandaEngine.createMaterial(data.materialType, data.material));
    if (data.url) {
        textureLoader.load(data.url, function (texture) {
            texture.wrapS = RepeatWrapping;
            texture.wrapT = RepeatWrapping;
            texture.repeat.set(data.planeSize, data.planeSize);
            plane.material.map = texture;
            plane.material.needsUpdate = true;
        });
    }
    if (data.userData){
        data.userData.selectable = false
    }else {
        data.userData = {selectable: false}
    }
    plane.name = data.name || '地面';
    return plane;
}


export function box(data: PandaCommponent): Object3D {
    const geometry = new BoxGeometry(data.width, data.height, data.depth, data.widthSegments, data.heightSegments, data.depthSegments);
    const mesh = new Mesh(geometry, PandaEngine.createMaterial(data.materialType, data.material));
    mesh.name = data.name || '立方体';
    return mesh;
}

export function pipe(data: PandaCommponent): Object3D {
    const curve:Curve<Vector3> = createPath(data.points)
    const tubeGeometry = new TubeGeometry(curve, 100,  data.radius, 16, false);
    const mesh = new Mesh(tubeGeometry, PandaEngine.createMaterial(data.materialType, data.material));
    mesh.name = data.name || '管道';
    return mesh;
}

export function plane(data: PandaCommponent): Object3D {
    const geometry = new PlaneGeometry(data.width, data.height, data.widthSegments, data.heightSegments);
    const mesh = new Mesh(geometry, PandaEngine.createMaterial(data.materialType, data.material));
    mesh.name = data.name || '平面';
    return mesh;
}

export function sprite(data: PandaCommponent): Object3D {
    const material = new PandaEngine.THREE.SpriteMaterial()
    const object = new Sprite(material)
    object.name = data.name || '精灵'
    return object;
}

export function sphere(data: PandaCommponent): Object3D {
    const geometry = new SphereGeometry(data.radius, data.widthSegments, data.heightSegments, data.phiStart, data.phiLength, data.thetaStart, data.thetaLength);
    const mesh = new Mesh(geometry, PandaEngine.createMaterial(data.materialType, data.material));
    mesh.name = data.name || '圆球体';
    return mesh;
}

export function cylinder(data: PandaCommponent): Object3D {
    const geometry = new CylinderGeometry(data.radiusTop, data.radiusBottom, data.height, data.radialSegments, data.heightSegments, data.openEnded, data.thetaStart, data.thetaLength);
    const mesh = new Mesh(geometry, PandaEngine.createMaterial(data.materialType, data.material));
    mesh.name = data.name || '圆柱体';
    return mesh;

}

export function text2d(data: PandaCommponent): Object3D {

    const text = new SpriteText(data.text, data.textHeight, data.color);
    if (data.backgroundColor) {
        text.backgroundColor = data.backgroundColor;
    }
    if (data.color) {
        text.color = data.color;
    }
    if (data.strokeWidth) {
        text.strokeWidth = data.strokeWidth;
    }
    if (data.strokeColor) {
        text.strokeColor = data.strokeColor;
    }
    if (data.fontFace) {
        text.fontFace = data.fontFace;
    }
    if (data.fontSize) {
        text.fontSize = data.fontSize;
    }
    if (data.fontWeight) {
        text.fontWeight = data.fontWeight;
    }
    if (data.padding) {
        text.padding = data.padding;
    }
    if (data.borderWidth) {
        text.borderWidth = data.borderWidth;
    }
    if (data.borderRadius) {
        text.borderRadius = data.borderRadius;
    }
    if (data.borderColor) {
        text.borderColor = data.borderColor;
    }
    text.scale.x = text.scale.x / text.scale.y;
    text.scale.y = 1;
    text.name = data.text;
    return text;
}


export function directionalLight(data: PandaCommponent): Object3D {
    const light = new DirectionalLight(data.color, data.intensity);
    light.name = '平行光';
    return light;
}

export function ambientLight(data: PandaCommponent): Object3D {
    const light = new AmbientLight(data.color, data.intensity);
    light.name = '环境光';
    return light;
}
export function hemisphereLight(data: PandaCommponent): Object3D {
    const light = new HemisphereLight(data.skyColor, data.groundColor, data.intensity);
    light.name = '半球光';
    return light;
}
export function pointLight(data: PandaCommponent): Object3D {
    const light = new PointLight(data.color, data.intensity, data.distance, data.decay);
    light.name = '点光源';
    if (data.power !== undefined || data.power !== null) {
        light.power = data.power;
    }
    return light;
}
export function spotLight(data: PandaCommponent): Object3D {
    const light = new SpotLight(data.color, data.intensity, data.distance, data.angle, data.penumbra, data.decay);
    light.name = '聚光灯';
    if (data.power !== undefined || data.power !== null) {
        light.power = data.power;
    }
    light.castShadow = true
    return light;
}
export function rectAreaLight(data: PandaCommponent): Object3D {
    const light = new RectAreaLight(data.color, data.intensity, data.width, data.height);
    light.name = '平面光光源';
    if (data.power !== undefined || data.power !== null) {
        light.power = data.power;
    }
    return light;
}

export function perspectiveCamera(data: PandaCommponent): Object3D {
    const camera = new PerspectiveCamera(data.fov, data.aspect, data.near, data.far);
    camera.lookAt(new Vector3());
    camera.name = '透视相机';
    return camera;
}
export function orthographicCamera(data: PandaCommponent): Object3D {
    const camera = new OrthographicCamera(data.left, data.right, data.top, data.bottom, data.near, data.far);
    camera.lookAt(new Vector3());
    camera.name = '正交相机';
    return camera;
}

export function fbx(data: PandaCommponent): Object3D|Promise<Object3D> {
    // const buf = fetch(data.url);
    const loader = new FBXLoader();

    // return object;
    return new Promise(async (resolve,reject)=>{
        try{
            const res=await fetch(data.url);
            const buf=await res.arrayBuffer();
            const object = loader.parse(buf as any, undefined);
            object.name = data.name || 'FBX模型';
            resolve(object);
        }catch(e){
            reject(e);
        }
    });
}

export function dae(data: PandaCommponent): Object3D|Promise<Object3D> {
    // const buf = fetch(data.url);
    const loader = new ColladaLoader();

    // return object;
    return new Promise(async (resolve,reject)=>{
        try{
            const res=await fetch(data.url);
            const buf=await res.arrayBuffer();
            const collada = loader.parse(buf as any, undefined);
            const object=collada.scene;
            object.name = data.name || 'DAE模型';
            resolve(object);
        }catch(e){
            reject(e);
        }
    });
}

export function glb(data: PandaCommponent): Object3D|Promise<Object3D> {
    const dracoLoader = new DRACOLoader();
    const ktx2Loader = new KTX2Loader();
    const loader = new GLTFLoader();
    loader.setDRACOLoader( dracoLoader );
    loader.setKTX2Loader( ktx2Loader );
    loader.setMeshoptDecoder( MeshoptDecoder );

    // return object;
    return new Promise(async (resolve,reject)=>{
        try{
            const res=await fetch(data.url);
            const buf=await res.arrayBuffer();
            loader.parse(buf as any, "",function ( result ) {
                const object = result.scene;
                object.name = data.name || 'glb模型';
                object.animations.push( ...result.animations );
                resolve(object);
            });
        }catch(e){
            reject(e);
        }
    });
}

export function gltf(data: PandaCommponent): Object3D|Promise<Object3D> {
    const dracoLoader = new DRACOLoader();
    const ktx2Loader = new KTX2Loader();
    // const buf = fetch(data.url);
    const loader = new GLTFLoader();
    loader.setDRACOLoader( dracoLoader );
    loader.setKTX2Loader( ktx2Loader );
    loader.setMeshoptDecoder( MeshoptDecoder );

    // return object;
    return new Promise(async (resolve,reject)=>{
        try{
            const res=await fetch(data.url);
            const buf=await res.arrayBuffer();
            loader.parse(buf as any, "",function ( result ) {
                const object = result.scene;
                object.name = data.name || 'glb模型';
                object.animations.push( ...result.animations );
                resolve(object);
            });
        }catch(e){
            reject(e);
        }
    });
}
